const focusOptionsSupport = new WeakMap();

function supportsFocusOptions(document) {
    if (focusOptionsSupport.has(document)) return focusOptionsSupport.get(document);
    let supported = false;
    try {
        // An unattached, non-focusable probe cannot change the active element.
        document.createElement('div').focus({
            get preventScroll() {
                supported = true;
                return true;
            },
        });
    } catch {
        // Older implementations can reject the options argument.
    }
    focusOptionsSupport.set(document, supported);
    return supported;
}

function captureScrollPositions(element) {
    const positions = [];
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
        positions.push({ element: parent, left: parent.scrollLeft, top: parent.scrollTop });
    }
    const scrollingElement = element.ownerDocument.scrollingElement;
    if (scrollingElement && !positions.some((position) => position.element === scrollingElement)) {
        positions.push({
            element: scrollingElement,
            left: scrollingElement.scrollLeft,
            top: scrollingElement.scrollTop,
        });
    }
    return positions;
}

function samePosition(first, second) {
    return first.left === second.left && first.top === second.top;
}

function restoreScrollPosition(position) {
    const scroller = position.element;
    const behavior = scroller.style.getPropertyValue('scroll-behavior');
    const priority = scroller.style.getPropertyPriority('scroll-behavior');
    scroller.style.setProperty('scroll-behavior', 'auto', 'important');
    try {
        // Same-value assignments also cancel a queued native smooth scroll.
        scroller.scrollLeft = position.left;
        scroller.scrollTop = position.top;
    } finally {
        if (behavior) scroller.style.setProperty('scroll-behavior', behavior, priority);
        else scroller.style.removeProperty('scroll-behavior');
    }
}

// Returns true when a synchronous native focus scroll needed correction.
// Callers can then resume an intentional scroll that the correction interrupted.
export function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return false;

    const document = element.ownerDocument;
    if (supportsFocusOptions(document)) {
        element.focus({ preventScroll: true });
        return false;
    }

    const view = document.defaultView;
    const positions = captureScrollPositions(element);
    const readPositions = () =>
        positions.map(({ element: scroller }) => ({
            left: scroller.scrollLeft,
            top: scroller.scrollTop,
        }));
    let atFocus;
    let afterHandlers;
    // Engines can scroll before or after focus events. Observe both boundaries
    // so ordinary focus handlers can keep their intentional scroll changes.
    const onFocus = (event) => {
        if (event.target === element) atFocus = readPositions();
    };
    const onFocusIn = (event) => {
        if (event.target === element) afterHandlers = readPositions();
    };
    view?.addEventListener('focus', onFocus, true);
    view?.addEventListener('focusin', onFocusIn);
    try {
        element.focus();
    } finally {
        view?.removeEventListener('focus', onFocus, true);
        view?.removeEventListener('focusin', onFocusIn);
    }

    // An already-focused/unfocusable target or redirected focus needs no correction.
    if (!atFocus || document.activeElement !== element) return false;
    const current = readPositions();
    const corrections = positions.map((position, index) => {
        if (afterHandlers && !samePosition(current[index], afterHandlers[index])) {
            return { element: position.element, ...afterHandlers[index] };
        }
        if (
            !samePosition(position, atFocus[index]) &&
            samePosition(current[index], atFocus[index])
        ) {
            return position;
        }
        return null;
    });
    if (!corrections.some(Boolean)) return false;

    positions.forEach((position, index) => {
        const correction = corrections[index];
        if (correction) restoreScrollPosition(correction);
        else if (samePosition(position, current[index]) && samePosition(position, atFocus[index])) {
            // Once a native jump is established, cancel pending smooth scrolling
            // on untouched ancestors as well; leave handler-controlled positions alone.
            restoreScrollPosition(position);
        }
    });
    return true;
}
